import { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Label,
  Radio,
  RadioGroup,
  Text,
} from '@fluentui/react-components'
import type { DemandStage, Demand } from '../types/demand'
import { useBroadcastReach, type BroadcastReach } from '../hooks/useDemands'
import { surfaces } from '../theme'
import { STAGE_LABELS } from '../lib/labels'

export type BroadcastAudience = 'fa_talent' | 'all_talent' | 'none'

/** Re-exported from the canonical labels module for back-compat with
 * existing imports. Source of truth lives in `lib/labels.ts`. */
export const STAGE_LABEL: Record<DemandStage, string> = STAGE_LABELS

export const TRANSITION_EFFECTS: Partial<Record<DemandStage, string>> = {
  scoping: 'Returns the demand to scoping — gathering info for triage.',
  triaging: 'Moves the demand to triaging for prioritization against the portfolio.',
  resourcing: 'Applications open, with a deadline 30 days out. Talent with matching skills get notified.',
  parking: 'The demand is parked. It can be re-opened later if circumstances change.',
  closed: 'The demand is closed. Pick a resolution type to record why.',
}

export interface TransitionDialogProps {
  open: boolean
  demand: Demand | null
  toStage: DemandStage | null
  onClose: () => void
  onConfirm: (notes: string, broadcastAudience?: BroadcastAudience) => void
  busy: boolean
}

/**
 * Shared stage-transition confirmation dialog.
 * Shows from→to badges, effect description, and requires a rationale note
 * that gets stored in DemandStageHistory.notes.
 *
 * When transitioning to open_to_application, shows a broadcast audience
 * selector (#757) so council can choose who to notify.
 */

/**
 * "(10 people)" — or nothing at all while the count is still loading.
 *
 * Deliberately no placeholder: a dash or a spinner where a number will appear
 * invites the reader to choose before the number lands, which is the exact
 * mistake the count exists to prevent.
 */
export function reachLabel(n?: number): string {
  if (n === undefined) return ''
  return ` — ${n} ${n === 1 ? 'person' : 'people'}`
}

/**
 * The focus-area option, which is not always an option.
 *
 * Talent declare FA1–FA4. BO is council-only and UN is the unclassified
 * holding bucket, so on a demand in either one there is no focus-area audience
 * to narrow down to — offering the choice and labelling it "0 people" states
 * the wrong reason for the same outcome, and sends the reader looking for
 * talent who could never have been there.
 */
export function faAudienceOption(
  focusAreaCode: string | null | undefined,
  reach?: BroadcastReach,
): { label: string; applicable: boolean } {
  const area = focusAreaCode || 'this focus area'
  if (reach?.fa_talent_scoped === false) {
    return { label: `Talents interested in ${area} — not applicable, talent cannot select ${area}`, applicable: false }
  }
  return { label: `Talents interested in ${area}${reachLabel(reach?.fa_talent)}`, applicable: true }
}

export function TransitionDialog({ open, demand, toStage, onClose, onConfirm, busy }: TransitionDialogProps) {
  const [notes, setNotes] = useState('')
  const [audience, setAudience] = useState<BroadcastAudience>('fa_talent')
  useEffect(() => {
    if (open) {
      setNotes('')
      setAudience('fa_talent')
    }
  }, [open])

  const fromLabel = demand ? STAGE_LABEL[demand.stage] : ''
  const toLabel = toStage ? STAGE_LABEL[toStage] : ''
  const effect = toStage ? TRANSITION_EFFECTS[toStage] ?? '' : ''
  const showBroadcast = toStage === 'resourcing' || toStage === 'open_to_application'
  // Declared after `showBroadcast` because it reads it — and fetched only while
  // the dialog is open on a posting move, so no other transition pays for it.
  const { data: reach } = useBroadcastReach(demand?.id, open && showBroadcast)
  const faOption = faAudienceOption(demand?.focus_area_code, reach)
  // The default is `fa_talent`, which on a BO or UN demand is a dead choice.
  // Move off it once the reach lands rather than letting Confirm send nothing.
  useEffect(() => {
    if (!faOption.applicable && audience === 'fa_talent') setAudience('all_talent')
  }, [faOption.applicable, audience])

  return (
    <Dialog open={open} onOpenChange={(_e, data) => { if (!data.open) onClose() }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Move to {toLabel}?</DialogTitle>
          <DialogContent>
            <Text block style={{ marginBottom: '12px' }}>
              <strong>{demand?.title ?? ''}</strong>
            </Text>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              marginBottom: '16px', padding: '10px 14px',
              background: surfaces.proposalCardBg, borderRadius: '6px', fontSize: '13px',
            }}>
              <Badge appearance="tint" color="brand">{fromLabel}</Badge>
              <span>→</span>
              <Badge appearance="filled" color="brand">{toLabel}</Badge>
            </div>
            {effect && (
              <Text block size={200} style={{ marginBottom: '12px', color: '#555' }}>
                {effect}
              </Text>
            )}
            {showBroadcast && (
              <div style={{ marginBottom: '16px', padding: '12px', background: '#fffbeb', borderRadius: '6px', border: '1px solid #fde68a' }}>
                <Label weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>
                  Notify talents about this opportunity?
                </Label>
                <RadioGroup value={audience} onChange={(_e, data) => setAudience(data.value as BroadcastAudience)}>
                  <Radio
                    value="fa_talent"
                    disabled={!faOption.applicable}
                    label={faOption.label}
                  />
                  <Radio
                    value="all_talent"
                    label={`All talents across DSAI${reachLabel(reach?.all_talent)}`}
                  />
                  <Radio value="none" label="Don't notify anyone" />
                </RadioGroup>
                {reach && faOption.applicable && audience !== 'none' &&
                  reach[audience === 'all_talent' ? 'all_talent' : 'fa_talent'] === 0 && (
                    <Text size={200} style={{ display: 'block', marginTop: '8px', color: '#a8442f' }}>
                      Nobody matches this audience — the demand would go live with no one told.
                    </Text>
                  )}
              </div>
            )}
            <textarea
              data-testid="transition-rationale"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Rationale (required) — visible in audit trail"
              rows={3}
              style={{ width: '100%', padding: '8px', fontFamily: 'inherit' }}
            />
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={() => onConfirm(notes, showBroadcast ? audience : undefined)}
              disabled={busy || notes.trim().length === 0}
            >
              {busy ? 'Moving…' : `Move to ${toLabel}`}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
